import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { AppConfigurationService } from '../../../app-configuration.service';
import { ContractType } from '../../../shared/models/contract/contract-type.model';
import { PostPartialCJModel } from '../../../shared/models/partial-cj/post-partial-cj';
import { StartUpConfigUiService } from '../../../shared/service/ui/start-up-config-ui.service';
import { PartialCjService } from '../partial-cj.service';
import { SectionWiseService } from '../section-wise.service';
import { AbTestConfigModel } from 'src/app/shared/models/start-up-config/abtest-config-model';

@Injectable({
  providedIn: 'root'
})
export class SwProcessStateService {
  private caid: string | null;
  private originalContract: ContractType = new ContractType();
  private swContractTypeSubject: BehaviorSubject<ContractType> = new BehaviorSubject<ContractType>(new ContractType());
  private returnCodeSubject = new BehaviorSubject<string | null>(null);

  public get swContractType$(): Observable<ContractType> {
    return this.swContractTypeSubject.asObservable();
  }

  public readonly returnCode$ = this.returnCodeSubject.asObservable();

  private isHealthContractLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get isHealthContractLoaded$(): Observable<boolean> {
    return this.isHealthContractLoadedSubject.asObservable();
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sectionWiseService: SectionWiseService,
    private startUpConfigUiService: StartUpConfigUiService,
    private configurationService: AppConfigurationService,
    private partialCJService: PartialCjService
  ) {}

  public initSwProcess(): Observable<void> {
    this.getHealthContractDetails();

    const queryParam = this.route.snapshot.queryParamMap;
    this.caid = queryParam.get('actid');

    const returnCode = queryParam.get('returncode');
    this.returnCodeSubject.next(returnCode);

    return new Observable((observer) => {
      this.startUpConfigUiService.sessionInfo$.subscribe((res) => {
        if (res.isCallCenter) {
          this.getPartialCJContractDetails();
        } else {
          this.getHealthContractDetails();
        }

        observer.next();
        observer.complete();
      });
    });
  }

  public updateSwProcessModel(updatedModel: ContractType): void {
    this.swContractTypeSubject.next(JSON.parse(JSON.stringify(updatedModel)));
  }

  public submitSwInput(abTestConfig: AbTestConfigModel): void {
    const returnCode = this.returnCodeSubject.value;
    this.sectionWiseService
      .postCjResponseSw(this.swContractTypeSubject.value, abTestConfig, returnCode)
      .subscribe(
        (res) => {
          if (!res.isSuccess) {
            this.startUpConfigUiService.redirectToCms();
          } else {
            this.redirectToProgress(res.caid);
          }
        },
        (err) => {
          console.error(err, JSON.stringify(this.swContractTypeSubject.value));
        }
      );
  }

  public savePartailCJContractDetails(request: PostPartialCJModel): Observable<boolean> {
    return new Observable((observer) => {
      request.partialCJRiskData = this.swContractTypeSubject.value;
      this.partialCJService.savePartialCJContractDetails(request).subscribe(
        (res) => {
          observer.next(res === true);
          observer.complete();
        },
        (error) => {
          console.error(error);
          observer.next(false);
          observer.complete();
        }
      );
    });
  }

  public discardSwInput(): void {
    if (this.caid) {
      this.redirectToProgress(this.caid);
    }
  }

  public isContractModified(): boolean {
    const currentContract = this.swContractTypeSubject.value;
    return JSON.stringify(currentContract) !== JSON.stringify(this.originalContract);
  }

  public isHealthContractLoaded(): boolean {
    let isLoaded = false;
    this.isHealthContractLoaded$.pipe(take(1)).subscribe((res: boolean) => {
      isLoaded = res;
    });
    return isLoaded;
  }

  private redirectToProgress(caid: string): void {
    // _v param added to avoid caching in view source for caid
    const cSig = new URLSearchParams(window.location.search).get('c-sig');
    const params: Params = {
      waitpagetype: 'isWaitPageFromSection',
      'c-sig': cSig,
      actid: caid
    };
    this.router.navigate(['/progress'], {
      queryParamsHandling: 'merge',
      queryParams: params
    });
  }

  private getHealthContractDetails() {
    this.sectionWiseService.getHealthContractDetails().subscribe({
      next: (contractType) => {
        if (contractType == null) {
          this.startUpConfigUiService.redirectToCms();
        }
        this.originalContract = JSON.parse(JSON.stringify(contractType));
        this.isHealthContractLoadedSubject.next(true);
        return this.swContractTypeSubject.next(contractType);
      },
      error: () => {
        this.isHealthContractLoadedSubject.next(true);
      }
    });
  }

  private getPartialCJContractDetails() {
    this.sectionWiseService.getPartialCJContractDetails().subscribe({
      next: () => {
        this.isHealthContractLoadedSubject.next(true);
      },
      error: () => {
        this.isHealthContractLoadedSubject.next(true);
      }
    });
  }
}
